// src/utils/familyOfficeReportPdf.js
// Family Office Report (§9.3 / §10 / §17 Phase 2) — reporting patrimonial consolidé.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import i18next from "i18next";
import { BRAND_UPPER } from "../lib/rebrand";

const tr = (key, fallback, options = {}) =>
  i18next.t(key, { defaultValue: fallback, ...options });

const currentLocale = () => {
  const lang = String(i18next.resolvedLanguage || i18next.language || "fr").split("-")[0];
  return lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "fr-FR";
};

const eur = (v) =>
  v || v === 0
    ? Number(v).toLocaleString(currentLocale(), { style: "currency", currency: "EUR" })
    : "—";

const priorityLabel = (priority) => {
  if (!priority) return "—";
  if (priority.key) {
    const fallback = priority.label || priority.key;
    return tr(`readiness.priority.${priority.key}`, fallback);
  }
  return priority.label || "—";
};

// families : [{ name, value, readiness (0-100), priority: {key,label} }]
// attention : { valuation, insurance, documentation, provenance, conservation, exceptional }
export function generateFamilyOfficeReport({ foName = "", overall = 0, families = [], attention = {} } = {}) {
  const doc = new jsPDF("p", "mm", "a4");
  const M = 16;
  let y = 18;

  const totalValue = families.reduce((s, f) => s + (Number(f.value) || 0), 0);

  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(150);
  doc.text(tr("familyOfficeReportPdf.header.eyebrow", "FAMILY OFFICE REPORT — PATRIMOINE ARTISTIQUE"), M, y);
  doc.setFontSize(18); doc.setTextColor(20);
  y += 9; doc.text(doc.splitTextToSize(foName || tr("familyOfficeReportPdf.header.defaultName", "Family Office"), 150), M, y);

  doc.setFont("helvetica", "bold"); doc.setFontSize(22);
  doc.setTextColor(overall >= 80 ? "#1a7a44" : overall >= 60 ? "#9a6b1f" : "#b4443a");
  doc.text(`${overall}%`, 210 - M, 22, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(150);
  doc.text(tr("familyOfficeReportPdf.header.portfolioReadiness", "Portfolio Readiness"), 210 - M, 27, { align: "right" });

  y += 4;
  autoTable(doc, {
    startY: y + 2, theme: "plain", margin: { left: M, right: M },
    styles: { fontSize: 10, cellPadding: 1.6 },
    columnStyles: { 0: { fontStyle: "bold", textColor: 120, cellWidth: 70 } },
    body: [
      [tr("familyOfficeReportPdf.summary.supervisedFamilies", "Familles supervisées"), String(families.length)],
      [tr("familyOfficeReportPdf.summary.consolidatedArtValue", "Valeur art consolidée"), eur(totalValue)],
    ],
  });
  y = (doc.lastAutoTable?.finalY || y) + 8;

  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
  doc.text(tr("familyOfficeReportPdf.families.title", "Préparation par famille"), M, y);
  autoTable(doc, {
    startY: y + 3, margin: { left: M, right: M },
    head: [[
      tr("familyOfficeReportPdf.families.columns.family", "Famille"),
      tr("familyOfficeReportPdf.families.columns.artValue", "Valeur art"),
      tr("familyOfficeReportPdf.families.columns.readiness", "Collection Readiness"),
      tr("familyOfficeReportPdf.families.columns.priority", "Priorité"),
    ]],
    body: families
      .slice()
      .sort((a, b) => (a.readiness || 0) - (b.readiness || 0))
      .map((f) => [f.name || "—", eur(f.value), f.readiness != null ? `${f.readiness} %` : "—", priorityLabel(f.priority)]),
    headStyles: { fillColor: [26, 26, 26], fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 1.8 },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
  });
  y = (doc.lastAutoTable?.finalY || y) + 8;

  if (y > 235) { doc.addPage(); y = 18; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
  doc.text(tr("familyOfficeReportPdf.attention.title", "Points d'attention (consolidé)"), M, y);
  autoTable(doc, {
    startY: y + 3, margin: { left: M, right: M },
    styles: { fontSize: 9.5, cellPadding: 1.8 },
    columnStyles: { 0: { textColor: 60 }, 1: { halign: "right", fontStyle: "bold", cellWidth: 30 } },
    body: [
      [tr("familyOfficeReportPdf.attention.valuation", "Valorisations à actualiser"), String(attention.valuation || 0)],
      [tr("familyOfficeReportPdf.attention.insurance", "Œuvres sous-assurées"), String(attention.insurance || 0)],
      [tr("familyOfficeReportPdf.attention.documentation", "Documentation à compléter"), String(attention.documentation || 0)],
      [tr("familyOfficeReportPdf.attention.provenance", "Provenance à compléter"), String(attention.provenance || 0)],
      [tr("familyOfficeReportPdf.attention.conservation", "Conservation à renseigner"), String(attention.conservation || 0)],
      [tr("familyOfficeReportPdf.attention.exceptional", "Œuvres exceptionnelles sans transmission"), String(attention.exceptional || 0)],
    ],
    theme: "plain",
  });
  y = (doc.lastAutoTable?.finalY || y) + 6;

  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  const foot = tr(
    "familyOfficeReportPdf.footer.disclaimer",
    "Reporting consolidé établi via " + BRAND_UPPER + " le {{date}}. Isolation stricte entre familles ; chaque famille reste maîtresse de ses données. " + BRAND_UPPER + " structure la donnée patrimoniale ; les décisions et validations relèvent des professionnels habilités.",
    { date: new Date().toLocaleDateString(currentLocale()) }
  );
  doc.text(doc.splitTextToSize(foot, 180), M, 290);

  const slug = (foName || "family-office").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  const prefix = tr("familyOfficeReportPdf.file.prefix", "family-office-report");
  doc.save(`${prefix}-${slug || "portfolio"}.pdf`);
}
